/**
 * Recent-songs LRU (pure — unit tested): the sheet bundles of the last ~10 opened songs are kept in
 * 'kgr-recent-v1' as a safety net for members who forget to save. Bounded by song count and bytes;
 * the most recently used song is always kept (even when it alone is over the byte limit).
 */
import type { RecentEntry } from './types';

export function entryBytes(e: RecentEntry): number {
  let n = 0;
  for (const u of e.urls) n += u.size;
  return n;
}

/** Records that `url` (of song `key`) was stored or served now. */
export function touchRecent(list: readonly RecentEntry[], key: string, now: number, url?: { url: string; size: number }): RecentEntry[] {
  const out = list.map((e) => ({ ...e, urls: [...e.urls] }));
  // A URL belongs to one entry only.
  if (url) {
    for (const e of out) if (e.key !== key) e.urls = e.urls.filter((u) => u.url !== url.url);
  }
  let hit = out.find((e) => e.key === key);
  if (!hit) {
    if (!url) return list.slice();
    hit = { key, urls: [], lastUsed: now };
    out.push(hit);
  }
  hit.lastUsed = now;
  if (url) {
    const i = hit.urls.findIndex((u) => u.url === url.url);
    if (i >= 0) hit.urls[i] = url;
    else hit.urls.push(url);
  }
  return out.filter((e) => e.urls.length > 0);
}

/** Which entries to keep and which to evict so count ≤ maxSongs and bytes ≤ maxBytes. */
export function evictRecent(list: readonly RecentEntry[], limits: { maxSongs: number; maxBytes: number }): { keep: RecentEntry[]; evicted: RecentEntry[] } {
  const sorted = [...list].sort((a, b) => b.lastUsed - a.lastUsed);
  const keep: RecentEntry[] = [];
  const evicted: RecentEntry[] = [];
  let bytes = 0;
  for (const e of sorted) {
    const b = entryBytes(e);
    if (keep.length === 0 || (keep.length < limits.maxSongs && bytes + b <= limits.maxBytes)) {
      keep.push(e);
      bytes += b;
    } else {
      evicted.push(e);
    }
  }
  return { keep, evicted };
}

/** Removes URLs (e.g. now stored in a pack, or 403/404 from the server) from the LRU. */
export function dropRecentUrls(list: readonly RecentEntry[], urls: ReadonlySet<string>): RecentEntry[] {
  return list.map((e) => ({ ...e, urls: e.urls.filter((u) => !urls.has(u.url)) })).filter((e) => e.urls.length > 0);
}
